import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material"
import FolderIcon from "@mui/icons-material/FolderOutlined"
import HomeIcon from "@mui/icons-material/HomeOutlined"

import { driveApi } from "../../api"
import { LoadingRows } from "../../components/LoadingState"

/**
 * Where to put it.
 *
 * ── Why the whole tree, flat and indented ───────────────────────────────────
 *
 * A picker that only offers the current folder's siblings makes moving
 * something two levels up a sequence of moves. The list is every folder the
 * person can open, drawn with its depth, which is the shape that lets somebody
 * find "Clients ▸ Northwind ▸ 2026" by reading rather than by navigating.
 *
 * ── What it refuses ─────────────────────────────────────────────────────────
 *
 * A folder cannot be moved inside itself or anything under it. The server
 * refuses it too — that is where the rule has to live — but offering the option
 * and then rejecting the click is a worse way to explain it than not offering
 * it, so the descendants are struck out of the list here.
 */
export default function MoveDialog({ item, clientId, onClose, onMoved }) {
  const [folders, setFolders] = useState(null)
  const [target, setTarget] = useState(0)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!item) return
    setError(null)
    setTarget(0)
    setFolders(null)
    driveApi
      .tree(clientId ? { client_id: clientId } : {})
      .then((payload) => setFolders(payload.folders || []))
      .catch(() => setFolders([]))
  }, [item, clientId])

  /* Depth-ordered, with each folder under its parent. Built here rather than
     asked of the server, because it is a drawing decision: the same flat rows
     are a tree on this screen and a list on the move menu. */
  const rows = useMemo(() => {
    if (!folders) return []
    const byParent = new Map()
    for (const folder of folders) {
      const key = Number(folder.parent_id)
      byParent.set(key, [...(byParent.get(key) || []), folder])
    }

    const forbidden = new Set()
    if (item?.kind === "folder") {
      // The folder itself and everything under it.
      const walk = (id) => {
        forbidden.add(id)
        for (const child of byParent.get(id) || []) walk(Number(child.id))
      }
      walk(Number(item.id))
    }

    const out = []
    const visit = (parentId, depth) => {
      for (const folder of byParent.get(parentId) || []) {
        out.push({ ...folder, depth, disabled: forbidden.has(Number(folder.id)) })
        if (depth < 12) visit(Number(folder.id), depth + 1)
      }
    }
    visit(0, 0)
    return out
  }, [folders, item])

  const move = async () => {
    setSaving(true)
    setError(null)
    try {
      if (item.kind === "folder") await driveApi.updateFolder(item.id, { ...(clientId ? { client_id: clientId } : {}), parent_id: target })
      else await driveApi.updateFile(item.id, { ...(clientId ? { client_id: clientId } : {}), folder_id: target })
      onMoved?.()
    } catch (err) {
      setError(err.userMessage || "That could not be moved.")
    } finally {
      setSaving(false)
    }
  }

  const currentParent = item?.kind === "folder" ? Number(item.parent_id) : Number(item?.folder_id)

  return (
    <Dialog open={Boolean(item)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Move “{item?.name}”
        <Typography variant="caption" color="text.secondary" display="block">
          Pick where it should live.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ maxHeight: 360 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {folders === null ? (
          <LoadingRows rows={4} height={28} />
        ) : (
          <Stack spacing={0.25}>
            <Row
              icon={HomeIcon}
              label="All files"
              depth={0}
              selected={target === 0}
              disabled={currentParent === 0}
              note={currentParent === 0 ? "where it is now" : null}
              onClick={() => setTarget(0)}
            />
            {rows.map((folder) => (
              <Row
                key={folder.id}
                icon={FolderIcon}
                label={folder.name}
                depth={folder.depth + 1}
                selected={target === Number(folder.id)}
                disabled={folder.disabled || currentParent === Number(folder.id)}
                note={
                  currentParent === Number(folder.id)
                    ? "where it is now"
                    : folder.disabled
                      ? "inside itself"
                      : null
                }
                onClick={() => setTarget(Number(folder.id))}
              />
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={move} disabled={saving || currentParent === target}>
          {saving ? "Moving…" : "Move here"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function Row({ icon: Icon, label, depth, selected, disabled, note, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        appearance: "none",
        border: 0,
        width: "100%",
        textAlign: "left",
        background: "none",
        font: "inherit",
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
        py: 0.75,
        pl: 1 + depth * 2,
        borderRadius: 1.5,
        cursor: disabled ? "default" : "pointer",
        /*
         * The row states its own ink.
         *
         * A <button> takes the browser's `buttontext` — black — unless it is
         * told otherwise, and a disabled one is dimmed again on top of that. In
         * the dark theme that made the destination's name invisible while the
         * grey note beside it, which set its colour explicitly, stayed
         * readable: the one row somebody needed to read was the one that had
         * disappeared. Both states are named here, and neither relies on a
         * default that differs per browser and per theme.
         */
        color: disabled ? "text.disabled" : "text.primary",
        opacity: 1,
        bgcolor: selected ? "action.selected" : "transparent",
        "&:hover": { bgcolor: disabled ? "transparent" : "action.hover" },
      }}
    >
      <Icon sx={{ fontSize: 18, color: "inherit", opacity: 0.75 }} />
      <Typography variant="body2" fontWeight={selected ? 700 : 500} noWrap>
        {label}
      </Typography>
      {note ? (
        <Typography variant="caption" color="text.disabled">
          {note}
        </Typography>
      ) : null}
    </Box>
  )
}
